
import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { WindowRefService } from '@services/window.service';
import { HttpClient } from '@angular/common/http';
import { SignalRService } from '@services/signalr.service';
import { AppComponent } from '../app.component';
import { ConnectionState, Device, GpsState, UiState } from '../pages/camera-realtime-map/models';
import { Subject, exhaustMap, lastValueFrom } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  icvEnabled: boolean = true;
  bwcEnabled: boolean = true;
  recordingEnabled: boolean = false;
  streamingEnabled: boolean = false;

  // Queues the processing of SignalR updates as
  // often a new update arrives before the previous one
  // is done processing
  updateQueue = new Subject();

  toggleRecording() {
    this.recordingEnabled = !this.recordingEnabled;
    this.emitFilteredDevices();
  }

  toggleStreaming() {
    this.streamingEnabled = !this.streamingEnabled;
    this.emitFilteredDevices();
  }

  toggleIcv() {
    this.icvEnabled = !this.icvEnabled;
    this.emitFilteredDevices();
  }

  toggleBwc() {
    this.bwcEnabled = !this.bwcEnabled;
    this.emitFilteredDevices();
  }

  private _devices: Device[] = [];
  searchText: string;
  userCompanyId: number = -1;
  cameraOfflineThreshold: number;
  gpsLocationStaleTimeout: number;
  gpsLocationLostTimeout: number;
  devicesUpdated = new EventEmitter<any>();

  set devices(value: Device[]) {
    this._devices = value;
    this.emitFilteredDevices();
  }

  get devices(): Device[] {
    return this._devices;
  }

  constructor(
    private signalRService: SignalRService,
    private ngZone: NgZone,
    private window: WindowRefService,
    private http: HttpClient,
    app: AppComponent) {

    this.userCompanyId = app.getAttribute("userCompanyId");
    this.cameraOfflineThreshold = app.getAttribute("cameraOfflineTimeout");
    this.gpsLocationStaleTimeout = app.getAttribute("gpsLocationStaleTimeout");
    this.gpsLocationLostTimeout = app.getAttribute("gpsLocationLostTimeout");
    this.updateQueue.pipe(exhaustMap((data: Device[]) => this.getFilteredDevices(data)))
      .subscribe(result => {
        this.devicesUpdated.emit(result);
      });

    // Load in the initial data
    this.getDevices().then((data: any[]) => {
      let devices = this.parseDeviceData(data);
      this.emitFilteredDevices(devices);
    });

    let signalREnabled: string = app.getAttribute("enableSignalR");

    // Subscribe to SignalR events, if SignalR is enabled
    if (JSON.parse(signalREnabled.toLowerCase())) {
      signalRService.startConnection('broadcastLiveDashBoardMessage');
      this.subscribeToEvents();
    }
  };

  private subscribeToEvents(): void {
    this.signalRService.messageReceived.subscribe((message: any) => {
      this.ngZone.run(() => {
        let devices = this.parseDeviceData(message);
        this.emitFilteredDevices(devices);
      });
    });
  }

  setDeviceHighlight(device: Device, state: boolean) {
    device.uiState.highlighted = state;
    this.emitFilteredDevices();
  }

  setDeviceSelected(device: Device) {
    this.deselectDevices();
    device.uiState.selected = true;
    this.emitFilteredDevices();
  }

  deselectDevices() {
    // If there is already a selected devices, unselect it
    if (this.devices.some((device: Device) => device.uiState.selected)) {
      this.devices.forEach((device: Device) => device.uiState.selected = false);
      this.emitFilteredDevices();
    }
  }

  searchDevices(searchText: string) {
    this.searchText = searchText;
    this.emitFilteredDevices();
  }

  clearSearch() {
    this.deselectDevices();
    this.searchDevices("");
    this.emitFilteredDevices();
  }


  private emitFilteredDevices(devices: Device[] = this.devices) {
    this.updateQueue.next(devices);
  }

  public getFilteredDevices(devices: Device[] = this.devices) : Promise<Device[]> {

    return new Promise((resolve) => {

      this.log(`Incoming devices ${devices.length}`);
      this.log(devices);
      this.log(`Cached devices ${this.devices.length}`);
      this.log(this.devices);

      // Devices that have come online (incoming devices that aren't in the cached devices)
      let added = devices.filter(d => !this.devices.some(e => e.id == d.id));

      this.log(`New devices adding to cache ${added.length}`);
      this.log(added);
      this.devices.push(...added);

      // We want to preserve this.devices as much as possible, as the more elements that change, the longer the redraw time
      // First detect changes between the cached devices and the incoming devices

      // Devices that have changed (GPS location, recording status etc) List includes devices that have just been added. This is quicker in two steps:
      // step 1- incoming devices that have matches in cached devices
      let same = devices.filter(d => this.devices.some(e => d.Equals(e)));
      this.log('Devices same ' + same.length);
      // step 2- incoming devices that aren't in the same device list
      let changed = devices.filter(d => !same.some(e => e.id == d.id));

      this.log(`Devices changed ${changed.length}`);
      this.log(changed);

      changed.forEach(d => {
        let deviceIndex = this.devices.findIndex(e => e.id == d.id);
        let device = this.devices[deviceIndex];
        device.gpsAgeOfData = d.gpsAgeOfData;
        device.gpsLocationTime = d.gpsLocationTime;
        device.lastSeenOnline = d.lastSeenOnline;
        device.gpsHeading = d.gpsHeading;
        device.gpsLatitude = d.gpsLatitude;
        device.gpsLongitude = d.gpsLongitude;
        device.isRecording = d.isRecording;
        device.isStreaming = d.isStreaming;
        device.triggerSource = d.triggerSource;
        device.triggerTime = d.triggerTime;
      });
      
      // Final list of updated, filtered devices to be emitted to any listeners
      let filteredDevices = this.devices
        // Only return devices that are online
        .filter(device => device.uiState.visible)

        .filter(device => device.connectionState == ConnectionState.Online)
        // Only return device types that are enabled in the UI filter
        .filter(device => (device.sourceType == 0 && this.icvEnabled) || (device.sourceType == 3 && this.bwcEnabled))
        // Only return recording & streaming states that are enabled in the UI filter
        .filter(device => !this.recordingEnabled || (this.recordingEnabled && device.isRecording))
        .filter(device => !this.streamingEnabled || (this.streamingEnabled && device.isStreaming))
        // If there is a string filter, only return devices that match the filter
        .filter(device => (device.name + device.companyName + device.id + device.companyId).toLowerCase().includes(this.searchText?.toLowerCase() ?? "")).sort((a, b) => b.name.localeCompare(a.name) * -1);

      this.log("Ending with")
      this.log(filteredDevices);

      resolve(filteredDevices);

    });
  }

  // Update the list of devices from the mView dashboard API data
  private parseDeviceData(updateData: any[]): Device[] {

    let updatedDevices: Device[] = updateData
      // Pre-filter. Remove devices that we'd never want to see so we don't have to spend time parsing them
      .reduce((acc: any, item: any) => {
        
        // If the device has never been online, ignore it
        if (!item.LastSeenOnline) {
          return acc;
        }

        // if the user is not an admin, only show devices belonging to their company
        if (this.userCompanyId != -1 && item.CompID != this.userCompanyId) {
          return acc;
        }

        acc.push(item);

        return acc;
      }, new Array())
      // Convert the incoming data to Device objects
      .map((item: any) => {
        return Object.assign(new Device(this.gpsLocationStaleTimeout, this.gpsLocationLostTimeout, this.cameraOfflineThreshold), {
          id: item.ID,
          name: item.dgn,
          deviceGroupUserId: item.DeviceGroupUserID,
          gpsAgeOfData: item.ag,
          gpsLatitude: item.la,
          gpsLongitude: item.ln,
          gpsLocationTime: new Date(item.lot),
          lastSeenOnline: new Date(item.LastSeenOnline),
          gpsHeading: item.hd,
          sourceType: item.srt,
          status: item.st,
          companyId: item.CompID,
          companyName: item.CompName,
          triggerTime: item.tt,
          triggerSource: item.ts,
          isStreaming: item.sm,
          isRecording: item.rc,
          uiState: new UiState(),
        });
      });

    return updatedDevices;

  }

  // Get list of devices from the API
  async getDevices(): Promise<Device[]> {
    const response = await this.http.post('/DeviceLiveLocation.aspx?endpoint=getdevices', null);
    const devices = await lastValueFrom(response) as Device[];
    return devices;
  }

  private log(message: any) {
    return;
    console.log(message);
  }
}
